import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiArchiveLine, RiSendPlaneLine } from '@remixicon/react'
import type { ArtifactDetail, ArtifactVersionSummary } from 'shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  MarkdownRenderer,
  PageHeader,
  SectionSpinner,
  Tab,
  Tabs,
  TabsList,
  TabsPanel,
  Textarea,
} from '../../components/ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { ARTIFACT_STATUS_TONE } from '../../lib/status'
import { formatDateTime } from '../../lib/dates'
import { DownloadReportPdf } from '../../components/report/DownloadReportPdf'

export default function OperatorReport() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { workspaceId, artifactId } = useParams<{ workspaceId: string; artifactId: string }>()
  const base = `/api/workspaces/${workspaceId}/artifacts/${artifactId}`

  const [artifact, setArtifact] = useState<ArtifactDetail | null>(null)
  const [workspaceName, setWorkspaceName] = useState<string>()
  const [versions, setVersions] = useState<ArtifactVersionSummary[]>([])
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [content, setContent] = useState('')
  const [changeNote, setChangeNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [detail, history, workspace] = await Promise.all([
      api.get<{ data: ArtifactDetail }>(base),
      api.get<{ data: ArtifactVersionSummary[] }>(`${base}/versions`),
      // Only for the PDF's footer: not worth failing the page over.
      api
        .get<{ data: { name: string } }>(`/api/workspaces/${workspaceId}`)
        .catch(() => null),
    ])
    setWorkspaceName(workspace?.data.name)
    setArtifact(detail.data)
    setVersions(history.data)
    setTitle(detail.data.title)
    setSummary(detail.data.summary ?? '')
    setContent(detail.data.version?.content_md ?? '')
    setChangeNote('')
  }, [base, workspaceId])

  useEffect(() => {
    if (!workspaceId || !artifactId) return
    load()
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [workspaceId, artifactId, load])

  // The body only travels when it actually changed: the backend records a new
  // version for every `content_md` it receives, and renaming a report should
  // not inflate its history.
  const save = async () => {
    if (!artifact) return
    const bodyChanged = content !== (artifact.version?.content_md ?? '')

    setSaving(true)
    setError(null)
    try {
      await api.put(base, {
        title: title.trim(),
        summary: summary.trim(),
        ...(bodyChanged ? { content_md: content, change_note: changeNote.trim() } : {}),
      })
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  const transition = async (action: 'publish' | 'archive') => {
    setSaving(true)
    setError(null)
    try {
      await api.post(`${base}/${action}`, {})
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <SectionSpinner />

  if (!artifact) {
    return (
      <EmptyState
        title={t('errors.notFound')}
        description={error ?? undefined}
        action={
          <Button variant="secondary" onClick={() => navigate(`/app/workspaces/${workspaceId}`)}>
            {t('common.back')}
          </Button>
        }
      />
    )
  }

  const publishedIsCurrent =
    artifact.published_version_id !== null &&
    artifact.published_version_id === artifact.current_version_id

  return (
    <>
      <PageHeader
        backTo={{ href: `/app/workspaces/${workspaceId}`, label: t('workspaces.backToWorkspace') }}
        title={artifact.title}
        subtitle={t('report.versionCount', { count: versions.length })}
        actions={
          <div className="flex items-center gap-2">
            <DownloadReportPdf artifact={artifact} content={content} workspaceName={workspaceName} />
            {artifact.status !== 'archived' && (
              <Button onClick={() => transition('publish')} loading={saving}>
                <RiSendPlaneLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
                {artifact.status === 'published'
                  ? t('report.publishUpdate')
                  : t('report.publish')}
              </Button>
            )}
            {artifact.status === 'published' && (
              <Button variant="secondary" onClick={() => transition('archive')} loading={saving}>
                <RiArchiveLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
                {t('report.archive')}
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Badge variant={ARTIFACT_STATUS_TONE[artifact.status]}>
          {t(`reports.status_${artifact.status}`)}
        </Badge>
        {artifact.created_by_agent && <Badge variant="default">{t('reports.byAgent')}</Badge>}
        {artifact.status === 'published' && !publishedIsCurrent && (
          <Badge variant="warning">{t('report.unpublishedChanges')}</Badge>
        )}
      </div>

      {error && <Alert className="mb-6">{error}</Alert>}

      <Tabs defaultValue="edit">
        <TabsList className="mb-6 w-full">
          <Tab value="edit">{t('report.tabs.edit')}</Tab>
          <Tab value="preview">{t('report.tabs.preview')}</Tab>
          <Tab value="history">{t('report.tabs.history')}</Tab>
        </TabsList>

        <TabsPanel value="edit">
          <Card className="space-y-6 p-6">
            <Field label={t('report.title')} required>
              <Input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </Field>

            <Field label={t('report.summary')}>
              <Textarea
                rows={2}
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
              />
            </Field>

            <Field label={t('report.content')}>
              <Textarea
                rows={22}
                className="font-mono text-xs"
                value={content}
                onChange={(event) => setContent(event.target.value)}
              />
            </Field>

            <Field label={t('report.changeNote')}>
              <Input
                value={changeNote}
                placeholder={t('report.changeNotePlaceholder')}
                onChange={(event) => setChangeNote(event.target.value)}
              />
            </Field>

            <div className="flex justify-end border-t border-gray-200 pt-6 dark:border-gray-800">
              <Button onClick={save} loading={saving}>
                {t('common.save')}
              </Button>
            </div>
          </Card>
        </TabsPanel>

        <TabsPanel value="preview">
          <Card className="p-6 sm:p-8">
            {content.trim() ? (
              <MarkdownRenderer content={content} />
            ) : (
              <EmptyState title={t('report.emptyTitle')} />
            )}
          </Card>
        </TabsPanel>

        <TabsPanel value="history">
          {versions.length === 0 ? (
            <Card className="p-6">
              <EmptyState title={t('report.historyEmpty')} />
            </Card>
          ) : (
            <Card className="divide-y divide-gray-100 dark:divide-gray-800">
              {versions.map((version) => (
                <div key={version.id} className="flex items-center gap-4 p-4">
                  <span className="w-12 shrink-0 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-50">
                    v{version.version_number}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-gray-900 dark:text-gray-50">
                      {version.change_note || t('report.noChangeNote')}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {formatDateTime(version.created_at, i18n.language)}
                      {version.created_by_name && ` · ${version.created_by_name}`}
                    </p>
                  </div>
                  {version.id === artifact.published_version_id && (
                    <Badge variant="success">{t('reports.status_published')}</Badge>
                  )}
                </div>
              ))}
            </Card>
          )}
        </TabsPanel>
      </Tabs>
    </>
  )
}
